import { BALANCE } from '../config/balance';
import type { RunMode } from '../core/types';
import type { Engine } from './engine';

export interface ScoreBreakdown {
  kills: number;
  seconds: number;
  anchors: number;
  evolutions: number;
  passiveCoverage: number;
  bossBonus: number;
  performance: number;
  build: number;
  total: number;
}

function countEvolutions(engine: Engine): number {
  let n = 0;
  for (const v of Object.values(engine.build.evolved)) if (v) n++;
  return n;
}

function countPassives(engine: Engine): number {
  let n = 0;
  for (const v of Object.values(engine.build.passive)) if (v > 0) n++;
  return n;
}

function bossBonusFor(engine: Engine): number {
  const s = BALANCE.score;
  let bonus = 0;
  if (engine.boss1KilledAt !== null) {
    const par = engine.mode === 'practice' ? s.boss1LessonPar : s.boss1LessonPar;
    const late = Math.max(0, engine.boss1KilledAt - par);
    bonus += Math.max(0, s.boss1Base - late * s.bossLatePerSecond);
  }
  if (engine.boss2KilledAt !== null) {
    const late = Math.max(0, engine.boss2KilledAt - s.boss2Par);
    bonus += Math.max(0, s.boss2Base - late * s.bossLatePerSecond);
  }
  return bonus;
}

export interface PseudoScoreStats {
  kills: number;
  seconds: number;
  evolutions: number;
  passiveCoverage: number;
  boss1KilledAt: number | null;
  boss2KilledAt: number | null;
}

/** 统计模型 AI 打分：与正式公式同一口径，输入为合成战绩。 */
export function scoreFromStats(stats: PseudoScoreStats): ScoreBreakdown {
  const s = BALANCE.score;
  const anchors = Math.floor(stats.seconds / 60);
  let bossBonus = 0;
  if (stats.boss1KilledAt !== null) {
    const late = Math.max(0, stats.boss1KilledAt - s.boss1LessonPar);
    bossBonus += Math.max(0, s.boss1Base - late * s.bossLatePerSecond);
  }
  if (stats.boss2KilledAt !== null) {
    const late = Math.max(0, stats.boss2KilledAt - s.boss2Par);
    bossBonus += Math.max(0, s.boss2Base - late * s.bossLatePerSecond);
  }
  const performance =
    stats.kills * s.kill + stats.seconds * s.second + anchors * s.anchor;
  const build = stats.evolutions * s.evolution
    + stats.passiveCoverage * s.passive + bossBonus;
  return {
    kills: stats.kills, seconds: stats.seconds, anchors,
    evolutions: stats.evolutions, passiveCoverage: stats.passiveCoverage,
    bossBonus, performance, build,
    total: Math.round(performance + build * 1.5),
  };
}

/** 总分：表现分 + 构筑分 ×1.5（构筑更值钱）。 */
export function computeScore(engine: Engine, _mode: RunMode): ScoreBreakdown {
  const s = BALANCE.score;
  const kills = engine.kills;
  const seconds = Math.floor(engine.time);
  const anchors = engine.anchors;
  const evolutions = countEvolutions(engine);
  const passiveCoverage = countPassives(engine);
  const bossBonus = bossBonusFor(engine);

  const performance =
    kills * s.kill + seconds * s.second + anchors * s.anchor;
  const build =
    evolutions * s.evolution + passiveCoverage * s.passive + bossBonus;
  const total = Math.round(performance + build * 1.5);

  return {
    kills, seconds, anchors, evolutions, passiveCoverage, bossBonus,
    performance, build, total,
  };
}
